import { Clock, ServerCrash, WifiOff } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'

import { ActionInfo } from './ActionInfo'
import { Button } from './Button'
import { DiagnosticCopy } from './DiagnosticCopy'

import './health.css'

/**
 * Точка «Связь» и её мини-окно (RP-UI-LINK). ui-kit не зависит от client-runtime (границы пакетов), поэтому типы ниже —
 * структурные двойники `HealthLevel`/`HealthSignal`/`HealthSummary` из `@krp/client-runtime`: сводку `summarize(...)`
 * приложение передаёт сюда как есть, а несовпадение словарей сломает сборку в месте вызова. Время форматирует
 * `formatMoment` client-runtime — компонент получает его пропсом и своей копии не держит.
 */
export type HealthDotLevel = 'ok' | 'degraded' | 'down' | 'unknown'

export interface HealthDotSignal {
  readonly subject: { readonly kind: string; readonly id: string; readonly title: string }
  readonly level: HealthDotLevel
  readonly summary: string
  readonly problem: string | null
  readonly lastOkAt: string | null
  readonly advice: string | null
  readonly actions: readonly { readonly key: string; readonly label: string }[]
  readonly diagnostics: string | null
}

export interface HealthDotSummary {
  readonly level: HealthDotLevel
  readonly signals: readonly HealthDotSignal[]
}

export interface HealthDotProps {
  summary: HealthDotSummary
  /** Слово рядом с точкой. */
  label?: string
  /** Момент словами («сегодня в 12:57») — `formatMoment` из client-runtime; `null` — не показывать. */
  formatMoment: (iso: string) => string | null
  /** Действие мини-окна: субъект сигнала и ключ действия; исполняет экран. */
  onAction?: (subjectId: string, key: string) => void
  /** Мини-окно открылось. */
  onOpen?: () => void
  /** Запись реестра действий для ⓘ у точки (UI-ACTIONS) — там, где реестр объявлен. */
  actionId?: string
}

/** Уровень словами — для диктора и тех, кто не различает цвета (CLAUDE.md §7). */
const LEVEL_WORDS: Readonly<Record<HealthDotLevel, string>> = {
  ok: 'всё в порядке',
  degraded: 'есть предупреждение',
  down: 'нет связи',
  unknown: 'ещё не проверялась',
}

/** Форма значка внутри точки: уровень различает не только цвет. */
const GLYPHS: Readonly<Record<HealthDotLevel, string>> = { ok: '✓', degraded: '!', down: '✕', unknown: '?' }

/** Пульсируют только жёлтая и красная: зелёная спокойна, когда всё хорошо (решение владельца 23.10.2026). */
const pulses = (level: HealthDotLevel) => level === 'degraded' || level === 'down'

/** Короткая подсказка при наведении: строка самого тяжёлого сигнала или «Всё отвечает». */
function healthHint(summary: HealthDotSummary): string {
  if (summary.level === 'ok') return 'Всё отвечает'
  const worst = summary.signals.find((signal) => signal.level === summary.level)
  if (worst !== undefined) return worst.summary
  return summary.level === 'unknown' ? 'Связь ещё не проверялась' : LEVEL_WORDS[summary.level]
}

function LevelGlyph({ level, pulse = false }: { level: HealthDotLevel; pulse?: boolean }) {
  return (
    <span
      className={pulse ? 'krp-health__dot krp-health__dot--pulse' : 'krp-health__dot'}
      data-level={level}
      aria-hidden="true"
    >
      {GLYPHS[level]}
    </span>
  )
}

/**
 * Одно поле «Связь» с точкой: цвет и значок по худшему уровню, наведение — короткая подсказка, нажатие — мини-окно
 * со строкой на каждый субъект. Правило «о связи говорим только при сбое» живёт здесь: при `ok` на экране только
 * спокойная зелёная точка.
 */
export function HealthDot({ summary, label = 'Связь', formatMoment, onAction, onOpen, actionId }: HealthDotProps) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLSpanElement>(null)
  const button = useRef<HTMLButtonElement>(null)
  const popoverId = useId()
  const { level } = summary

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      button.current?.focus()
    }
    const onPointer = (event: PointerEvent) => {
      if (event.target instanceof Node && root.current?.contains(event.target) === true) return
      setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointer)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointer)
    }
  }, [open])

  const toggle = () => {
    const next = !open
    setOpen(next)
    if (next) onOpen?.()
  }

  const trigger = (
    <button
      ref={button}
      type="button"
      className="krp-health__button"
      data-level={level}
      aria-label={`${label}: ${LEVEL_WORDS[level]}`}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-controls={open ? popoverId : undefined}
      title={healthHint(summary)}
      onClick={toggle}
    >
      <LevelGlyph level={level} pulse={pulses(level)} />
      <span className="krp-health__label">{label}</span>
    </button>
  )

  return (
    <span className="krp-health" ref={root}>
      {actionId === undefined ? trigger : <ActionInfo id={actionId} label={label}>{trigger}</ActionInfo>}
      {open && (
        <HealthPopover
          id={popoverId}
          title={label}
          summary={summary}
          formatMoment={formatMoment}
          {...(onAction !== undefined ? { onAction } : {})}
        />
      )}
    </span>
  )
}

export interface HealthPopoverProps {
  id?: string
  title?: string
  summary: HealthDotSummary
  formatMoment: (iso: string) => string | null
  onAction?: (subjectId: string, key: string) => void
}

/**
 * Мини-окно «Связь» рядом с точкой — не на весь экран: строка на субъект (значок, название, состояние), причина службы
 * дословно (ADR-8), последний ответ, совет, кнопки действий и «Скопировать» для администратора. Ошибка не исчезает по
 * таймеру: окно закрывает только человек (Esc, нажатие мимо, повторное нажатие на точку).
 */
export function HealthPopover({ id, title = 'Связь', summary, formatMoment, onAction }: HealthPopoverProps) {
  const diagnostics = summary.signals
    .map((signal) => (signal.diagnostics === null ? null : `${signal.subject.title}\n${signal.diagnostics}`))
    .filter((text): text is string => text !== null)
  return (
    <div id={id} className="krp-health__popover" role="dialog" aria-label={title} data-level={summary.level}>
      {summary.signals.length === 0 ? (
        <p className="krp-health__empty">Связь ещё не проверялась.</p>
      ) : (
        <ul className="krp-health__list">
          {summary.signals.map((signal) => {
            const lastOk = signal.lastOkAt === null ? null : formatMoment(signal.lastOkAt)
            return (
              <li key={`${signal.subject.kind}:${signal.subject.id}`} className="krp-health__item" data-level={signal.level}>
                <div className="krp-health__head">
                  <LevelGlyph level={signal.level} />
                  <span className="krp-health__subject">{signal.subject.title}</span>
                  <span className="krp-health__sr">: {LEVEL_WORDS[signal.level]}</span>
                </div>
                <p className="krp-health__summary">{signal.summary}</p>
                {signal.problem !== null && <p className="krp-health__problem">{signal.problem}</p>}
                {lastOk !== null && <p className="krp-health__meta">Последний ответ: {lastOk}</p>}
                {signal.advice !== null && <p className="krp-health__advice">{signal.advice}</p>}
                {signal.actions.length > 0 && (
                  <div className="krp-health__actions">
                    {signal.actions.map((action) => (
                      <Button key={action.key} variant="secondary" data-size="sm" onClick={() => onAction?.(signal.subject.id, action.key)}>
                        {action.label}
                      </Button>
                    ))}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
      {diagnostics.length > 0 && <DiagnosticCopy text={diagnostics.join('\n\n')} label="Скопировать для администратора" />}
    </div>
  )
}

export interface LinkStripProps {
  /** Вид строки: значок и слово различают его без цвета (CLAUDE.md §7). */
  kind: 'offline' | 'down' | 'stale'
  /** Готовая строка из модели здоровья: «Нет связи 12 с · попытка 3 · обновлено 52 с назад». */
  text: string
  /** Название строки для диктора. */
  label: string
  /** «Повторить сейчас»; нет — кнопки нет. */
  onRetry?: (() => void) | null
  retryLabel: string
  /** Дополнительный класс места, где строка стоит (отступы под раскладку экрана). */
  className?: string
}

const STRIP_ICONS = { offline: WifiOff, down: ServerCrash, stale: Clock } as const

/**
 * Строка связи внутри экрана продажи и окна оплаты (RP-SALE-FAST, шаг 5): вместо общего окна по центру, которое перекрывало «Отменить»
 * и «Свернуть». Рисуется только при сбое — в норме родитель её не монтирует. Диктор получает появление строки; счётчик, который
 * меняется каждую секунду, он не зачитывает (`aria-live="off"` у текста).
 */
export function LinkStrip({ kind, text, label, onRetry, retryLabel, className }: LinkStripProps) {
  const Icon = STRIP_ICONS[kind]
  return (
    <div className={className === undefined ? 'krp-link-strip' : `krp-link-strip ${className}`} role="alert" aria-label={label} data-kind={kind}>
      <Icon className="krp-link-strip__icon" size={16} aria-hidden="true" />
      <span className="krp-link-strip__text" aria-live="off">
        {text}
      </span>
      {onRetry != null && (
        <Button variant="secondary" data-size="sm" onClick={onRetry}>
          {retryLabel}
        </Button>
      )}
    </div>
  )
}
